import { collection, getDocs, query, where, writeBatch } from 'firebase/firestore'
import { db } from '@/firebase/init'

const BATCH_SIZE = 500

/**
 * The three customer price collections. The pricing document's shape is
 * identical across all three customer types — the same fact
 * `customerPricingAdapters.ts` relies on.
 */
const CUSTOMER_PRICE_COLLECTIONS = [
  'CustomerAccountPrices',
  'InsuranceCustomerAccountPrices',
  'CustomerPrivatePrices',
] as const

/**
 * Clears customer-level pricing overrides for a lab, leaving the segment
 * assignment intact.
 *
 * The counterpart to `runSeedCustomerPricing`, and deliberately narrower than
 * `runClear`'s own reset: this is for re-seeding pricing while keeping the
 * customers on their segment, so the tabs still have something to inherit.
 * `runClear` additionally nulls `pricingSegmentID`, because by then the segment
 * itself has been deleted.
 */
export async function runClearCustomerPricing(
  accountID: string
): Promise<number> {
  let cleared = 0

  for (const collectionName of CUSTOMER_PRICE_COLLECTIONS) {
    const snapshot = await getDocs(
      query(
        collection(db, 'Apps', '-lab-management-', collectionName),
        where('labAccountID', '==', accountID)
      )
    )
    if (snapshot.empty) continue

    const docs = snapshot.docs
    for (let i = 0; i < docs.length; i += BATCH_SIZE) {
      const batch = writeBatch(db)
      docs.slice(i, i + BATCH_SIZE).forEach((priceDoc) => {
        batch.update(priceDoc.ref, {
          analyses: [],
          tats: [],
          addons: [],
          deals: [],
          'timestamps.updatedAt': Date.now(),
        })
      })
      await batch.commit()
      cleared += docs.slice(i, i + BATCH_SIZE).length
    }
  }

  return cleared
}
